/**
 * Every route path in the app, in one place.
 *
 * The router (`main.tsx`), the nav (`components/nav/constants/navItems.ts`) and
 * every `navigate()` call read from here, so changing a URL is a single edit
 * instead of a hunt across the router, the sidebar and each page that links to
 * it.
 *
 * `ROUTES` holds the literal paths — including the `:param` patterns the router
 * registers. Anything parameterised also gets a builder in `buildRoute` so no
 * caller has to re-interpolate the pattern by hand.
 */
export const ROUTES = {
  HOME: "/",
  SIGN_IN: "/sign-in",

  OUTPATIENT: "/outpatient",
  OUTPATIENT_EDIT: "/outpatient/edit",
  EDIT: "/edit",

  SITES: "/sites",
  SITE_DETAIL: "/sites/:id",

  RADIOLOGISTS: "/radiologists",
  RADIOLOGIST_DETAIL: "/radiologists/:id",

  RAD_SUPPORT: "/rad-support",
  RAD_SUPPORT_DETAIL: "/rad-support/:id",

  SPECIALTIES: "/specialties",

  STUDIES: "/studies",
  STUDY_DETAIL: "/studies/:studyId",

  HEALTH: "/health",
  USERS: "/users",
  FORECAST: "/forecast",
  ANALYTICS: "/analytics",
  ROSTER: "/roster",
  INTAKE: "/intake",
  ONBOARDING: "/onboarding",
  VACATIONS: "/vacations",
  SUPPORT_SCHEDULE: "/support-schedule",

  AUTOMATION: "/automation",
  TI_TELEMETRY: "/ti-telemetry",

  /** Catch-all; redirects to HOME. */
  NOT_FOUND: "*",
} as const;

/** Paths for the routes that take a parameter. */
export const buildRoute = {
  site: (id: number) => `${ROUTES.SITES}/${id}`,
  radiologist: (id: number) => `${ROUTES.RADIOLOGISTS}/${id}`,
  radSupport: (id: number) => `${ROUTES.RAD_SUPPORT}/${id}`,
  // Study ids come from external systems, so they are not safe raw in a URL.
  study: (studyId: string) => `${ROUTES.STUDIES}/${encodeURIComponent(studyId)}`,
} as const;
